import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Building2, Mail, Calendar, Tag, Search, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fetchCustomersByFactory, createCustomer, updateCustomer, deleteCustomer, type Customer, type CreateCustomerInput } from '@/core/api';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Skeleton } from '@/shared/ui/skeleton';
import { Badge } from '@/shared/ui/badge';
import { useToast } from '@/shared/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';

/**
 * Customers page - List and manage external customers.
 */
export const CustomersPage = () => {
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    internal_code: '',
    organization_code: '',
    contact_email: '',
    customer_since: '',
  });

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ['customers', selectedTenantId, selectedFactoryId],
    queryFn: () => {
      if (!selectedFactoryId) return [];
      return fetchCustomersByFactory(selectedFactoryId);
    },
    enabled: !!selectedFactoryId,
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateCustomerInput) => createCustomer(input),
    onSuccess: () => {
      toast({
        title: 'Customer created',
        description: 'Customer has been added successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      resetForm();
      setCreateDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create customer',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateCustomerInput> }) =>
      updateCustomer(id, data),
    onSuccess: () => {
      toast({
        title: 'Customer updated',
        description: 'Customer has been updated successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setEditDialogOpen(false);
      setSelectedCustomer(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update customer',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCustomer(id),
    onSuccess: () => {
      toast({
        title: 'Customer deleted',
        description: 'Customer has been removed successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setDeleteDialogOpen(false);
      setSelectedCustomer(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete customer',
        variant: 'destructive',
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      internal_code: '',
      organization_code: '',
      contact_email: '',
      customer_since: '',
    });
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTenantId || !selectedFactoryId) {
      toast({
        title: 'Error',
        description: 'Please select a tenant and factory',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.name.trim()) {
      toast({
        title: 'Error',
        description: 'Customer name is required',
        variant: 'destructive',
      });
      return;
    }

    createMutation.mutate({
      tenant_id: selectedTenantId,
      factory_id: selectedFactoryId,
      name: formData.name,
      description: formData.description || null,
      internal_code: formData.internal_code || null,
      organization_code: formData.organization_code || null,
      contact_email: formData.contact_email || null,
      customer_since: formData.customer_since || null,
    });
  };

  const handleEdit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCustomer) return;

    updateMutation.mutate({
      id: selectedCustomer.id,
      data: {
        name: formData.name,
        description: formData.description || null,
        internal_code: formData.internal_code || null,
        organization_code: formData.organization_code || null,
        contact_email: formData.contact_email || null,
        customer_since: formData.customer_since || null,
      },
    });
  };

  const openEditDialog = (customer: Customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name,
      description: customer.description || '',
      internal_code: customer.internal_code || '',
      organization_code: customer.organization_code || '',
      contact_email: customer.contact_email || '',
      customer_since: customer.customer_since || '',
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDeleteDialogOpen(true);
  };

  const filteredCustomers = customers.filter((customer) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      customer.name.toLowerCase().includes(term) ||
      customer.internal_code?.toLowerCase().includes(term) ||
      customer.organization_code?.toLowerCase().includes(term) ||
      customer.contact_email?.toLowerCase().includes(term)
    );
  });

  if (!selectedFactoryId) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Please select a factory to view customers.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground mt-1">
            Manage your external customers and their information
          </p>
        </div>

        {/* Create Button */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button size="lg" onClick={resetForm}>
              <Plus className="h-5 w-5 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Customer</DialogTitle>
              <DialogDescription>
                Create a new customer record. Only the name is required.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">
                  Customer Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="create-name"
                  placeholder="e.g., BMW Automotive"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-internal-code">Internal Code</Label>
                  <Input
                    id="create-internal-code"
                    placeholder="e.g., BMW-001"
                    value={formData.internal_code}
                    onChange={(e) => setFormData({ ...formData, internal_code: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="create-org-code">Organization Code</Label>
                  <Input
                    id="create-org-code"
                    placeholder="e.g., DE123456"
                    value={formData.organization_code}
                    onChange={(e) =>
                      setFormData({ ...formData, organization_code: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-email">Contact Email</Label>
                <Input
                  id="create-email"
                  type="email"
                  placeholder="e.g., contact@bmw.com"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-since">Customer Since</Label>
                <Input
                  id="create-since"
                  type="date"
                  value={formData.customer_since}
                  onChange={(e) => setFormData({ ...formData, customer_since: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-description">Description</Label>
                <Textarea
                  id="create-description"
                  placeholder="Additional information about the customer..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create Customer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search customers by name, code, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customer List */}
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : filteredCustomers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {customers.length === 0 ? (
              <div className="space-y-2">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50" />
                <p>No customers yet. Create your first customer to get started.</p>
              </div>
            ) : (
              <p>No customers match your search.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredCustomers.map((customer) => (
            <Card key={customer.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Building2 className="h-5 w-5 text-primary" />
                      {customer.name}
                    </CardTitle>
                    {customer.description && (
                      <CardDescription className="mt-2 line-clamp-2">
                        {customer.description}
                      </CardDescription>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(customer)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(customer)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {customer.internal_code && (
                    <Badge variant="secondary" className="gap-1">
                      <Tag className="h-3 w-3" />
                      {customer.internal_code}
                    </Badge>
                  )}
                  {customer.organization_code && (
                    <Badge variant="outline" className="gap-1">
                      <Tag className="h-3 w-3" />
                      {customer.organization_code}
                    </Badge>
                  )}
                </div>

                {customer.contact_email && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <a
                      href={`mailto:${customer.contact_email}`}
                      className="hover:text-primary hover:underline"
                    >
                      {customer.contact_email}
                    </a>
                  </div>
                )}

                {customer.customer_since && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>
                      Customer since {format(new Date(customer.customer_since), 'MMM yyyy')}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
            <DialogDescription>Update customer information.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">
                Customer Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="edit-name"
                placeholder="e.g., BMW Automotive"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-internal-code">Internal Code</Label>
                <Input
                  id="edit-internal-code"
                  placeholder="e.g., BMW-001"
                  value={formData.internal_code}
                  onChange={(e) => setFormData({ ...formData, internal_code: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-org-code">Organization Code</Label>
                <Input
                  id="edit-org-code"
                  placeholder="e.g., DE123456"
                  value={formData.organization_code}
                  onChange={(e) => setFormData({ ...formData, organization_code: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Contact Email</Label>
              <Input
                id="edit-email"
                type="email"
                placeholder="e.g., contact@bmw.com"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-since">Customer Since</Label>
              <Input
                id="edit-since"
                type="date"
                value={formData.customer_since}
                onChange={(e) => setFormData({ ...formData, customer_since: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                placeholder="Additional information about the customer..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Updating...' : 'Update Customer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedCustomer?.name}</strong>? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedCustomer && deleteMutation.mutate(selectedCustomer.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
